import { useState, useRef, useCallback } from "react";
import { URL_CONSTANTS } from "../../axios/urlLinksThemes";
import { httpPost } from "../../axios/axiosUtils";
import {
  getBussinessData,
  getCustomerInfoFromStroge,
  showErrortMessage,
  showSuccesstMessage,
} from "../../shared/helpersMethod";
import {
  getPaymentData,
  IAqsatiCustomer,
  IGATEWAYS,
  savePaymentData,
  IRESAllData,
} from "../../shared/commonUtils";
import { useAppDispatch, useAppSelector } from "../useReduxMethods";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { setPaymentData } from "../../store/slices/gateways-slice";
import useGetToken from "../useGetToken";

const useGateWays = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const { getToken } = useGetToken();
  const customerInfo = getCustomerInfoFromStroge();
  const { bussinessData } = useAppSelector((state) => state.bussinessData);

  // Prevent multiple simultaneous API calls
  const apiCallInProgress = useRef(false);
  const dataFetched = useRef(false);

  // Dummy response (kept as is)
  var aqsatiDummyRes = {
    type: "success",
    identity: "6330153657143355",
    customer: {
      customerName: "حسن حافظ عبيد ناصر",
      phoneNumber: "7707833940",
      installmentOptions: {
        minMonth: 1,
        maxMonth: 36,
        netPayment: 6000000,
        dedections: 5005059.67,
        allowableSalary: 994940.33,
        countOfMonth: 36,
      },
    },
    trnid: "2024Oct08092157_9210cb2ce7046c48d7ec161fec772182",
  };

  const [billingGateWays, setBillingGateWays] = useState<IGATEWAYS | undefined>(
    {}
  );
  const [transactionId, setTransactionId] = useState<string>("");

  // Helper function to safely check if businessPaymentGateways exists
  const getBusinessPaymentGatewaysRedisData = () => {
    try {
      if (
        typeof window !== "undefined" &&
        window.businessPaymentGateways &&
        window.businessPaymentGateways !== null &&
        window.businessPaymentGateways !== undefined &&
        window.businessPaymentGateways !== "__PAYMENT_GATEWAYS__" &&
        typeof window.businessPaymentGateways === "object"
      ) {
        return window.businessPaymentGateways;
      }
    } catch (error) {
      console.warn("Error accessing businessPaymentGateways:", error);
    }
    return null;
  };

  const getBillingGateWays = useCallback(
    async (retryAfterTokenRefresh = false): Promise<void> => {
      // Prevent multiple simultaneous calls (but allow retry after token refresh)
      if (apiCallInProgress.current && !retryAfterTokenRefresh) {
        console.log("Payment gateways API call already in progress, skipping");
        return;
      }

      // Skip Redis and cache data if this is a retry after token refresh
      if (!retryAfterTokenRefresh) {
        // Check for cached Redis data first
        const businessPaymentGatewaysRedisData =
          getBusinessPaymentGatewaysRedisData();
        if (businessPaymentGatewaysRedisData && !dataFetched.current) {
          console.log("Using cached Redis payment gateways data");
          try {
            setBillingGateWays(businessPaymentGatewaysRedisData);
            dispatch(setPaymentData(businessPaymentGatewaysRedisData));
            savePaymentData(businessPaymentGatewaysRedisData);
            dataFetched.current = true;

            return;
          } catch (error) {
            console.error(
              "Error processing Redis payment gateways data:",
              error
            );
            // Continue to check cache data if Redis data processing fails
          }
        }

        //   // If no Redis data, check for local storage cache data
        //   const cacheData = getPaymentData();
        //   if (cacheData !== null && !dataFetched.current) {
        //     console.log("Using cached local storage payment gateways data");
        //     setBillingGateWays(cacheData);
        //     dispatch(setPaymentData(cacheData));
        //     dataFetched.current = true;
        //     return;
        //   }
        //
      }

      // Get business token
      let bussinessData = getBussinessData();
      if (!bussinessData?.accessToken) {
        console.log(
          "No business token found for payment gateways, attempting to get token"
        );
        try {
          const newToken = await getToken();
          // Update business token after getting new token
          bussinessData = getBussinessData();
          if (!bussinessData?.accessToken) {
            console.error(
              "Still no token after getToken call for payment gateways"
            );
            return;
          }
        } catch (error) {
          console.error("Failed to get token for payment gateways:", error);
          return;
        }
      }

      // No cache data, fetch from API
      apiCallInProgress.current = true;
      setLoading(true);

      try {
        console.log("🚀 Making API call to get payment gateways data");
        const res: IRESAllData = await httpPost(
          URL_CONSTANTS.get("URL_GET_GATEWAYS")!,
          {},
          {},
          bussinessData.accessToken
        );

        if (res.type === "error" && res?.token === "expired") {
          console.log(
            "🔄 Payment gateways token expired, refreshing token and retrying"
          );
          try {
            // Reset the API call flag to allow retry
            apiCallInProgress.current = false;

            const newToken = await getToken(true); // Force token refresh
            console.log(
              "✅ New token obtained for payment gateways:",
              newToken ? "Success" : "Failed"
            );

            // Retry the API call with new token
            if (newToken) {
              console.log(
                "🔄 Retrying payment gateways API call with new token"
              );
              return getBillingGateWays(true); // Pass true to indicate this is a retry
            } else {
              throw new Error("Failed to get new token for payment gateways");
            }
          } catch (tokenError) {
            console.error(
              "❌ Failed to refresh token for payment gateways:",
              tokenError
            );
            throw tokenError;
          }
        }

        if (res.type === "success") {
          console.log("✅ Successfully fetched payment gateways data");
          setBillingGateWays(res.gateways);
          dispatch(setPaymentData(res.gateways));
          savePaymentData(res.gateways);
          dataFetched.current = true;
        } else {
          console.error("❌ Payment gateways API returned error:", res);
          console.log("Error: Failed to load payment gateways");
          // showErrortMessage("Failed to load payment gateways");
        }
      } catch (error: any) {
        console.error("❌ Payment gateways API call failed:", error);
        console.log("Error: Failed to load payment gateways");
        // showErrortMessage("Error loading payment gateways");
      } finally {
        setLoading(false);
        apiCallInProgress.current = false;
      }
    },
    [getToken, dispatch]
  );

  return {
    getBillingGateWays,
    billingGateWays,
    gateWaysLoading: loading,
  };
};

export default useGateWays;
